export interface WasmProbe {
  present: boolean;
  simd: boolean;
}

export type WebGLClassification = 'hardware' | 'software' | 'none' | 'unknown' | 'error';

export type WebGpuClassification = 'absent' | 'adapter-info-missing' | 'usable';

export interface WebGLProbe {
  webgl2: boolean;
  webgl1: boolean;
  renderer: string | null;
  vendor: string | null;
  classification: WebGLClassification;
}

// Byte-for-byte the effects-sdk 3.12.1 bundle's SIMD feature-test module.
const WASM_SIMD_TEST_MODULE = new Uint8Array([
  0, 97, 115, 109, 1, 0, 0, 0, 1, 4, 1, 96, 0, 0, 3, 2, 1, 0, 10, 30, 1, 28, 0, 65, 0, 253, 15, 253,
  12, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 253, 186, 1, 26, 11,
]);

// Both lists copied verbatim from the effects-sdk 3.12.1 bundle's detectSoftware —
// classifier drift against the SDK pollutes the sdkDisagreement signal.
const HARDWARE_RENDERERS = [
  'nvidia',
  'geforce',
  'quadro',
  'rtx',
  'tegra',
  'amd',
  'radeon',
  'intel',
  'iris',
  'arc',
  'apple m',
  'apple a',
  'apple gpu',
  'adreno',
  'qualcomm',
  'snapdragon',
  'mali',
  'immortalis',
  'powervr',
  'vivante',
  'xclipse',
  'videocore',
  'broadcom',
  'moore threads',
  'mthreads',
  'jingjia',
  'biren',
  'innosilicon',
  'glenfly',
  'loongson',
  'zhaoxin',
];

const SOFTWARE_RENDERERS = [
  'swiftshader',
  'llvmpipe',
  'softpipe',
  'mesa offscreen',
  'microsoft basic render',
  'warp',
  'software adapter',
  'apple software renderer',
  'gdi generic',
];

export function probeWasm(): WasmProbe {
  const present =
    typeof WebAssembly !== 'undefined' && typeof WebAssembly.instantiate === 'function';
  let simd = false;
  if (present) {
    try {
      simd = WebAssembly.validate(WASM_SIMD_TEST_MODULE);
    } catch {
      simd = false;
    }
  }
  return { present, simd };
}

type ProbedContext = WebGLRenderingContext | WebGL2RenderingContext;

// Mirrors the SDK's detectSoftware: hardware allow-list checked first and winning.
const classify = (renderer: string, vendor: string): 'hardware' | 'software' | 'unknown' => {
  const haystack = `${renderer} ${vendor}`.toLowerCase();
  if (!haystack.trim()) {
    return 'unknown';
  }
  if (HARDWARE_RENDERERS.some((keyword) => haystack.includes(keyword))) {
    return 'hardware';
  }
  if (SOFTWARE_RENDERERS.some((keyword) => haystack.includes(keyword))) {
    return 'software';
  }
  return 'unknown';
};

const readContextInfo = (gl: ProbedContext) => {
  const plainRenderer = String(gl.getParameter(gl.RENDERER) ?? '');
  const plainVendor = String(gl.getParameter(gl.VENDOR) ?? '');
  let classification = classify(plainRenderer, plainVendor);
  let renderer = plainRenderer || null;
  let vendor = plainVendor || null;

  if (classification === 'unknown') {
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const unmaskedRenderer = String(gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) ?? '');
      const unmaskedVendor = String(gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) ?? '');
      classification = classify(unmaskedRenderer, unmaskedVendor);
      renderer = unmaskedRenderer || renderer;
      vendor = unmaskedVendor || vendor;
    }
  }

  return { renderer, vendor, classification };
};

export function probeWebGL(): WebGLProbe {
  const contexts: ProbedContext[] = [];
  try {
    // Mirror the SDK read exactly: webgl2 then webgl, no context attributes.
    // Two canvases: a canvas refuses a second context of a different type.
    const gl2 = document.createElement('canvas').getContext('webgl2');
    if (gl2) {
      contexts.push(gl2);
    }
    const gl1 = document.createElement('canvas').getContext('webgl');
    if (gl1) {
      contexts.push(gl1);
    }

    if (!gl2 && !gl1) {
      return { webgl2: false, webgl1: false, renderer: null, vendor: null, classification: 'none' };
    }

    const { renderer, vendor, classification } = readContextInfo((gl2 ?? gl1) as ProbedContext);
    return { webgl2: !!gl2, webgl1: !!gl1, renderer, vendor, classification };
  } catch {
    return { webgl2: false, webgl1: false, renderer: null, vendor: null, classification: 'error' };
  } finally {
    // Browsers cap live WebGL contexts (~16/page); a leaked probe context could CAUSE
    // the failure being measured — mandatory with the re-probe loop.
    for (const gl of contexts) {
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    }
  }
}

// 'adapter-info-missing' is the cohort ORT cannot serve: Safari shipped WebGPU in 26.0 but
// `GPUDevice.adapterInfo` only in a later 26.x, and ORT's WebGPU glue reads it unguarded on the
// device the SDK injects. 'absent' is healthy — the SDK falls back to WASM and reports 3001.
export function probeWebGpu(): WebGpuClassification {
  const gpuDevice = (globalThis as { GPUDevice?: { prototype: object } }).GPUDevice;
  if (!gpuDevice) {
    return 'absent';
  }
  return 'adapterInfo' in gpuDevice.prototype ? 'usable' : 'adapter-info-missing';
}
